import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { NoticeModel } from "features/stock-ordering/core/domain/notice.model";
import {
  GetUnreadNoticesRepository,
  GetUnreadNoticesResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetUnreadNoticesState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetUnreadNoticesState;
  message: string;
  data: NoticeModel[] | undefined;
}

const initialState: InitialState = {
  status: GetUnreadNoticesState.initial,
  message: "",
  data: undefined,
};

export const getUnreadNotices = createAsyncThunk(
  "getUnreadNotices",
  async (_, { rejectWithValue }) => {
    try {
      const response: GetUnreadNoticesResponse =
        await GetUnreadNoticesRepository();
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

export const getUnreadNoticesSlice = createSlice({
  name: "getUnreadNotices",
  initialState,
  reducers: {
    resetGetUnreadNotices: (state) => {
      state.status = GetUnreadNoticesState.initial;
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUnreadNotices.pending, (state) => {
        state.status = GetUnreadNoticesState.inProgress;
      })
      .addCase(getUnreadNotices.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetUnreadNoticesState.success;
          state.message = message;
          state.data = data.notices;
        }
      })
      .addCase(getUnreadNotices.rejected, (state, action) => {
        state.status = GetUnreadNoticesState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetUnreadNotices = (state: RootState) =>
  state.getUnreadNotices;
export const { resetGetUnreadNotices } = getUnreadNoticesSlice.actions;
export default getUnreadNoticesSlice.reducer;
